import React, { useState, useEffect, useCallback } from 'react';
import { FileText, Plus, Search, Pencil, Trash2, ArrowRightLeft, Download, CreditCard, Clock, CheckCircle2, XCircle, Send, Wrench, Printer, MessageCircle, Package, Globe, Mail } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { toast } from 'sonner';
import { API } from '@/utils/api';
import axios from 'axios';
import AdminLayout from '@/components/admin/AdminLayout';
import ClientSearchSelect from '@/components/ClientSearchSelect';
import { useTranslation } from 'react-i18next';

const TVA_RATE = 8.1;

const QUOTE_STATUSES = [
  { value: 'brouillon', label: 'Brouillon', color: 'bg-slate-100 text-slate-700' },
  { value: 'envoye', label: 'Envoyé', color: 'bg-blue-100 text-blue-700' },
  { value: 'accepte', label: 'Accepté', color: 'bg-green-100 text-green-700' },
  { value: 'refuse', label: 'Refusé', color: 'bg-red-100 text-red-700' },
  { value: 'expire', label: 'Expiré', color: 'bg-amber-100 text-amber-700' },
  { value: 'facture', label: 'Facturé', color: 'bg-violet-100 text-violet-700' },
];

const INVOICE_STATUSES = [
  { value: 'brouillon', label: 'Brouillon', color: 'bg-slate-100 text-slate-700' },
  { value: 'envoyee', label: 'Envoyée', color: 'bg-blue-100 text-blue-700' },
  { value: 'payee', label: 'Payée', color: 'bg-green-100 text-green-700' },
  { value: 'en_retard', label: 'En retard', color: 'bg-red-100 text-red-700' },
  { value: 'annulee', label: 'Annulée', color: 'bg-slate-100 text-slate-500' },
];

const emptyLine = { description: '', quantity: 1, unit_price: 0, tva_rate: TVA_RATE };

const StatusBadge = ({ status, type }) => {
  const list = type === 'quote' ? QUOTE_STATUSES : INVOICE_STATUSES;
  const s = list.find(x => x.value === status) || { label: status, color: 'bg-slate-100 text-slate-600' };
  return <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${s.color}`} data-testid={`status-${status}`}>{s.label}</span>;
};

const BillingPage = ({ auth, onLogout }) => {
  const { t } = useTranslation();
  const [tab, setTab] = useState('quotes');
  const [quotes, setQuotes] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [stats, setStats] = useState(null);
  const [clients, setClients] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formType, setFormType] = useState('quote');
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState({ client_id: '', vehicle_id: '', lines: [{ ...emptyLine }], conditions: 'Payable a 30 jours', validity_days: 30, notes: '' });
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [clientVehicles, setClientVehicles] = useState([]);
  const [settings, setSettings] = useState({ labor_rates: [] });
  const [showSendDialog, setShowSendDialog] = useState(null);
  const [sendOnlineLoading, setSendOnlineLoading] = useState(null);
  const [laborPopoverOpen, setLaborPopoverOpen] = useState(false);
  const [partsPopoverOpen, setPartsPopoverOpen] = useState(false);
  const [partsSearch, setPartsSearch] = useState('');
  const [partsResults, setPartsResults] = useState([]);
  const [partsCatalog, setPartsCatalog] = useState([]);
  const [laborCatalog, setLaborCatalog] = useState([]);
  const [laborRates, setLaborRates] = useState({});
  const [catalogFilter, setCatalogFilter] = useState('');

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [qRes, iRes, sRes, cRes, setRes, pCatRes, lCatRes] = await Promise.all([
        axios.get(`${API}/billing/quotes`),
        axios.get(`${API}/billing/invoices`),
        axios.get(`${API}/billing/stats`),
        axios.get(`${API}/clients`),
        axios.get(`${API}/billing/settings`),
        axios.get(`${API}/billing/catalog/parts`).catch(() => ({ data: [] })),
        axios.get(`${API}/billing/catalog/labor`).catch(() => ({ data: { labor: [], rates: {} } })),
      ]);
      setQuotes(qRes.data);
      setInvoices(iRes.data);
      setStats(sRes.data);
      setClients(cRes.data);
      setSettings(setRes.data);
      setPartsCatalog(pCatRes.data);
      setLaborCatalog(lCatRes.data.labor || []);
      setLaborRates(lCatRes.data.rates || {});
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const loadClientVehicles = async (clientId) => {
    if (!clientId) { setClientVehicles([]); return; }
    try {
      const res = await axios.get(`${API}/vehicles`, { params: { client_id: clientId } });
      setClientVehicles(res.data);
    } catch { setClientVehicles([]); }
  };

  const openAdd = (type) => {
    setFormType(type);
    setEditItem(null);
    const defaultDueDate = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
    setForm({ client_id: '', vehicle_id: '', lines: [{ ...emptyLine }], conditions: 'Payable a 30 jours', validity_days: 30, notes: '', payment_due_date: defaultDueDate });
    setClientVehicles([]);
    setShowForm(true);
  };

  const openEdit = (item, type) => {
    setFormType(type);
    setEditItem(item);
    setForm({
      client_id: item.client_id || '',
      vehicle_id: item.vehicle_id || '',
      lines: item.lines?.length ? item.lines.map(l => ({ ...l })) : [{ ...emptyLine }],
      conditions: item.conditions || 'Payable a 30 jours',
      validity_days: item.validity_days || 30,
      notes: item.notes || '',
      payment_due_date: item.payment_due_date || '',
    });
    loadClientVehicles(item.client_id);
    setShowForm(true);
  };

  const addLine = () => setForm(f => ({ ...f, lines: [...f.lines, { ...emptyLine }] }));
  const addLaborLine = (labor) => {
    const rate = laborRates[labor.rate_type] || labor.hourly_rate || 140;
    setForm(f => ({
      ...f,
      lines: [...f.lines, { description: labor.name || labor.label, quantity: labor.default_hours || 1, unit_price: rate, tva_rate: TVA_RATE }]
    }));
  };
  const addCatalogPart = (part) => {
    setForm(f => ({
      ...f,
      lines: [...f.lines, { description: part.name, quantity: 1, unit_price: part.unit_price || 0, tva_rate: TVA_RATE }]
    }));
    setPartsPopoverOpen(false);
    setCatalogFilter('');
  };
  const searchParts = async (q) => {
    setPartsSearch(q);
    if (q.length < 2) { setPartsResults([]); return; }
    try {
      const res = await axios.get(`${API}/inventory/parts`, { params: { search: q } });
      setPartsResults(res.data.slice(0, 10));
    } catch { setPartsResults([]); }
  };
  const addPartLine = (part) => {
    const desc = `${part.name}${part.brand ? ` ${part.brand}` : ''}${part.reference ? ` (Réf: ${part.reference})` : ''}`;
    setForm(f => ({
      ...f,
      lines: [...f.lines, { description: desc, quantity: 1, unit_price: part.sell_price || 0, tva_rate: TVA_RATE }]
    }));
    setPartsPopoverOpen(false);
    setPartsSearch('');
    setPartsResults([]);
  };
  const removeLine = (idx) => setForm(f => ({ ...f, lines: f.lines.filter((_, i) => i !== idx) }));
  const updateLine = (idx, field, value) => {
    setForm(f => {
      const lines = [...f.lines];
      lines[idx] = { ...lines[idx], [field]: value };
      return { ...f, lines };
    });
  };

  const calcTotal = (lines) => {
    let sub = 0, tva = 0;
    for (const l of lines) {
      const ht = (l.quantity || 0) * (l.unit_price || 0);
      sub += ht;
      tva += ht * (l.tva_rate || TVA_RATE) / 100;
    }
    return { subtotal: sub.toFixed(2), tva: tva.toFixed(2), total: (sub + tva).toFixed(2) };
  };

  const handleSave = async () => {
    if (!form.client_id) { toast.error('Sélectionnez un client'); return; }
    if (!form.lines.length || !form.lines[0].description) { toast.error('Ajoutez au moins une ligne'); return; }
    try {
      const endpoint = formType === 'quote' ? 'quotes' : 'invoices';
      if (editItem) {
        await axios.put(`${API}/billing/${endpoint}/${editItem.id}`, form);
        toast.success(formType === 'quote' ? 'Devis mis a jour' : 'Facture mise a jour');
      } else {
        await axios.post(`${API}/billing/${endpoint}`, form);
        toast.success(formType === 'quote' ? 'Devis créé' : 'Facture créée');
      }
      setShowForm(false);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    try {
      const endpoint = deleteConfirm.type === 'quote' ? 'quotes' : 'invoices';
      await axios.delete(`${API}/billing/${endpoint}/${deleteConfirm.id}`);
      toast.success('Supprime');
      setDeleteConfirm(null);
      loadData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const handleStatusChange = async (item, type, newStatus) => {
    try {
      const endpoint = type === 'quote' ? 'quotes' : 'invoices';
      const data = { status: newStatus };
      if (newStatus === 'payee') data.payment_date = new Date().toISOString().slice(0, 10);
      await axios.put(`${API}/billing/${endpoint}/${item.id}`, data);
      toast.success('Statut mis a jour');
      loadData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const handleConvert = async (quoteId) => {
    try {
      await axios.post(`${API}/billing/quotes/${quoteId}/convert`);
      toast.success('Devis converti en facture');
      loadData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur de conversion'); }
  };

  const openPdf = async (type, id) => {
    try {
      const endpoint = type === 'quote' ? 'quotes' : 'invoices';
      const { downloadPdf } = await import('@/utils/api');
      await downloadPdf(`${API}/billing/${endpoint}/${id}/pdf`, `${type === 'quote' ? 'devis' : 'facture'}_${id.substring(0, 8)}.pdf`);
    } catch (err) {
      toast.error('Erreur lors du telechargement du PDF');
    }
  };

  const handleSendWhatsApp = async (type, id) => {
    try {
      const endpoint = type === 'quote' ? 'quotes' : 'invoices';
      const res = await axios.post(`${API}/billing/${endpoint}/${id}/whatsapp-send`);
      if (res.data.success) {
        toast.success(`${type === 'quote' ? 'Devis' : 'Facture'} envoyé(e) par WhatsApp à ${res.data.recipient || 'client'}`);
      } else {
        toast.error('Token WhatsApp expiré — Renouvelez-le dans Meta Business Manager');
      }
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur WhatsApp — Vérifiez le token'); }
  };

  const handleSendEmail = async (type, id) => {
    try {
      const endpoint = type === 'quote' ? 'quotes' : 'invoices';
      const res = await axios.post(`${API}/billing/${endpoint}/${id}/email-send`);
      if (res.data.success) {
        toast.success(`${type === 'quote' ? 'Devis' : 'Facture'} envoyé(e) par email à ${res.data.recipient || res.data.email}`);
        loadData();
      } else {
        toast.error('Erreur lors de l\'envoi par email');
      }
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur email — Vérifiez l\'adresse du client'); }
  };

  const handlePrint = async (type, id) => {
    try {
      const endpoint = type === 'quote' ? 'quotes' : 'invoices';
      const { downloadPdf } = await import('@/utils/api');
      await downloadPdf(`${API}/billing/${endpoint}/${id}/pdf`, `${type === 'quote' ? 'devis' : 'facture'}-${id}.pdf`);
    } catch (err) {
      toast.error('Erreur lors du téléchargement');
    }
  };

  const sendQuoteOnline = async (quoteId, channel = 'email') => {
    setSendOnlineLoading(quoteId);
    try {
      const res = await axios.post(`${API}/quotes-online/send/${quoteId}`, { channel });
      if (res.data.success) {
        toast.success(`Devis envoyé en ligne par ${channel}`);
      } else {
        toast.error(res.data.error || 'Token WhatsApp expiré — Renouvelez-le dans Meta Business Manager');
      }
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur envoi en ligne');
    } finally {
      setSendOnlineLoading(null);
    }
  };

  const filteredQuotes = quotes.filter(q =>
    !search || q.number?.toLowerCase().includes(search.toLowerCase()) ||
    q.client_name?.toLowerCase().includes(search.toLowerCase())
  );
  const filteredInvoices = invoices.filter(i =>
    !search || i.number?.toLowerCase().includes(search.toLowerCase()) ||
    i.client_name?.toLowerCase().includes(search.toLowerCase())
  );

  const totals = calcTotal(form.lines);
  const activeLaborRates = (settings.labor_rates || []).filter(r => r.hourly_rate > 0);

  return (
    <AdminLayout auth={auth} onLogout={onLogout}>
      <div className="p-4 md:p-6 space-y-6" data-testid="billing-page">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
          <div>
            <h1 className="text-2xl font-bold text-slate-900" data-testid="billing-title">{t('billing.title')}</h1>
            <p className="text-sm text-slate-500">Module independant de facturation</p>
          </div>
          <div className="flex gap-2">
            <Button onClick={() => openAdd('quote')} className="bg-blue-600 hover:bg-blue-700" data-testid="add-quote-btn">
              <Plus className="w-4 h-4 mr-1" /> Nouveau devis
            </Button>
            <Button onClick={() => openAdd('invoice')} variant="outline" data-testid="add-invoice-btn">
              <Plus className="w-4 h-4 mr-1" /> Nouvelle facture
            </Button>
          </div>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Card className="p-4" data-testid="stat-quotes">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-50 rounded-lg"><FileText className="w-5 h-5 text-blue-600" /></div>
                <div>
                  <p className="text-xs text-slate-500">Devis</p>
                  <p className="text-lg font-bold">{stats.quotes_count}</p>
                  <p className="text-xs text-slate-400">{stats.quotes_pending} en attente</p>
                </div>
              </div>
            </Card>
            <Card className="p-4" data-testid="stat-invoices">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-emerald-50 rounded-lg"><CreditCard className="w-5 h-5 text-emerald-600" /></div>
                <div>
                  <p className="text-xs text-slate-500">Factures</p>
                  <p className="text-lg font-bold">{stats.invoices_count}</p>
                  <p className="text-xs text-slate-400">{stats.invoices_unpaid} non payees</p>
                </div>
              </div>
            </Card>
            <Card className="p-4" data-testid="stat-total-invoiced">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-50 rounded-lg"><Clock className="w-5 h-5 text-amber-600" /></div>
                <div>
                  <p className="text-xs text-slate-500">Total facture</p>
                  <p className="text-lg font-bold">CHF {stats.total_invoiced?.toFixed(2)}</p>
                </div>
              </div>
            </Card>
            <Card className="p-4" data-testid="stat-total-paid">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-50 rounded-lg"><CheckCircle2 className="w-5 h-5 text-green-600" /></div>
                <div>
                  <p className="text-xs text-slate-500">Total paye</p>
                  <p className="text-lg font-bold">CHF {stats.total_paid?.toFixed(2)}</p>
                </div>
              </div>
            </Card>
          </div>
        )}

        {/* Search */}
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input placeholder="Rechercher par numéro ou client..." value={search} onChange={e => setSearch(e.target.value)}
            className="pl-10" data-testid="billing-search" />
        </div>

        {/* Tabs */}
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList data-testid="billing-tabs">
            <TabsTrigger value="quotes" data-testid="tab-quotes">{t('billing.quotes')} ({filteredQuotes.length})</TabsTrigger>
            <TabsTrigger value="invoices" data-testid="tab-invoices">{t('billing.invoices')} ({filteredInvoices.length})</TabsTrigger>
          </TabsList>

          {/* QUOTES TAB */}
          <TabsContent value="quotes" className="mt-4">
            {loading ? <p className="text-slate-500 text-center py-8">{t('common.loading')}</p> :
              filteredQuotes.length === 0 ? <p className="text-slate-500 text-center py-8">Aucun devis</p> : (
                <div className="space-y-2">
                  {filteredQuotes.map(q => (
                    <Card key={q.id} className="p-4 hover:shadow-md transition-shadow" data-testid={`quote-${q.id}`}>
                      <div className="flex flex-col sm:flex-row justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-mono font-bold text-blue-700">{q.number}</span>
                            <StatusBadge status={q.status} type="quote" />
                          </div>
                          <p className="text-sm text-slate-700">{q.client_name}</p>
                          {q.vehicle_info && <p className="text-xs text-slate-500">{q.vehicle_info}</p>}
                          <p className="text-xs text-slate-400 mt-1">{q.created_at?.slice(0, 10)} | {q.lines?.length || 0} ligne(s)</p>
                        </div>
                        <div className="flex flex-col items-end gap-2">
                          <p className="text-lg font-bold text-slate-900">CHF {q.total?.toFixed(2)}</p>
                          <div className="flex gap-1 flex-wrap justify-end">
                            <Button size="sm" variant="ghost" title="PDF" onClick={() => openPdf('quote', q.id)} data-testid={`pdf-quote-${q.id}`}><Download className="w-3.5 h-3.5" /></Button>
                            <Button size="sm" variant="ghost" title="Imprimer" onClick={() => handlePrint('quote', q.id)}><Printer className="w-3.5 h-3.5" /></Button>
                            <Button size="sm" variant="ghost" title="WhatsApp" className="text-green-600" onClick={() => handleSendWhatsApp('quote', q.id)} data-testid={`wa-quote-${q.id}`}><MessageCircle className="w-3.5 h-3.5" /></Button>
                            <Button size="sm" variant="ghost" title="Envoyer par email" className="text-blue-600" onClick={() => handleSendEmail('quote', q.id)} data-testid={`email-quote-${q.id}`}><Mail className="w-3.5 h-3.5" /></Button>
                            {q.status !== 'facture' && q.status !== 'refuse' && (
                              <Button size="sm" variant="ghost" title="Envoyer en ligne" className="text-blue-600" onClick={() => sendQuoteOnline(q.id, 'email')} disabled={sendOnlineLoading === q.id} data-testid={`online-quote-${q.id}`}>
                                <Globe className="w-3.5 h-3.5 mr-1" /> En ligne
                              </Button>
                            )}
                            {q.status !== 'facture' && (
                              <>
                                <Button size="sm" variant="ghost" onClick={() => openEdit(q, 'quote')}><Pencil className="w-3.5 h-3.5" /></Button>
                                {q.status === 'brouillon' && <Button size="sm" variant="ghost" onClick={() => handleStatusChange(q, 'quote', 'envoye')}><Send className="w-3.5 h-3.5" /></Button>}
                                {(q.status === 'envoye' || q.status === 'accepte') && (
                                  <Button size="sm" variant="ghost" className="text-violet-600" onClick={() => handleConvert(q.id)} data-testid={`convert-${q.id}`}>
                                    <ArrowRightLeft className="w-3.5 h-3.5 mr-1" /> Facturer
                                  </Button>
                                )}
                                <Button size="sm" variant="ghost" className="text-red-500" onClick={() => setDeleteConfirm({ ...q, type: 'quote' })}><Trash2 className="w-3.5 h-3.5" /></Button>
                              </>
                            )}
                          </div>
                          {q.status !== 'facture' && q.status !== 'brouillon' && (
                            <Select value={q.status} onValueChange={(v) => handleStatusChange(q, 'quote', v)}>
                              <SelectTrigger className="w-32 h-7 text-xs"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                {QUOTE_STATUSES.filter(s => s.value !== 'facture').map(s => (
                                  <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              )}
          </TabsContent>

          {/* INVOICES TAB */}
          <TabsContent value="invoices" className="mt-4">
            {loading ? <p className="text-slate-500 text-center py-8">{t('common.loading')}</p> :
              filteredInvoices.length === 0 ? <p className="text-slate-500 text-center py-8">Aucune facture</p> : (
                <div className="space-y-2">
                  {filteredInvoices.map(inv => (
                    <Card key={inv.id} className="p-4 hover:shadow-md transition-shadow" data-testid={`invoice-${inv.id}`}>
                      <div className="flex flex-col sm:flex-row justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-mono font-bold text-emerald-700">{inv.number}</span>
                            <StatusBadge status={inv.status} type="invoice" />
                            {inv.quote_id && <span className="text-xs text-violet-500">depuis devis</span>}
                          </div>
                          <p className="text-sm text-slate-700">{inv.client_name}</p>
                          {inv.vehicle_info && <p className="text-xs text-slate-500">{inv.vehicle_info}</p>}
                          <p className="text-xs text-slate-400 mt-1">{inv.created_at?.slice(0, 10)} | Échéance : {inv.payment_due_date || '-'}</p>
                        </div>
                        <div className="flex flex-col items-end gap-2">
                          <p className="text-lg font-bold text-slate-900">CHF {inv.total?.toFixed(2)}</p>
                          <div className="flex gap-1 flex-wrap justify-end">
                            <Button size="sm" variant="ghost" title="PDF" onClick={() => openPdf('invoice', inv.id)} data-testid={`pdf-invoice-${inv.id}`}><Download className="w-3.5 h-3.5" /></Button>
                            <Button size="sm" variant="ghost" title="Imprimer" onClick={() => handlePrint('invoice', inv.id)} data-testid={`print-invoice-${inv.id}`}><Printer className="w-3.5 h-3.5" /></Button>
                            <Button size="sm" variant="ghost" title="WhatsApp" className="text-green-600" onClick={() => handleSendWhatsApp('invoice', inv.id)} data-testid={`wa-invoice-${inv.id}`}><MessageCircle className="w-3.5 h-3.5" /></Button>
                            <Button size="sm" variant="ghost" title="Envoyer par email" className="text-blue-600" onClick={() => handleSendEmail('invoice', inv.id)} data-testid={`email-invoice-${inv.id}`}><Mail className="w-3.5 h-3.5" /></Button>
                            {inv.status !== 'payee' && inv.status !== 'annulee' && (
                              <>
                                <Button size="sm" variant="ghost" onClick={() => openEdit(inv, 'invoice')}><Pencil className="w-3.5 h-3.5" /></Button>
                                <Button size="sm" variant="ghost" className="text-green-600" onClick={() => handleStatusChange(inv, 'invoice', 'payee')} data-testid={`pay-${inv.id}`}>
                                  <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Payer
                                </Button>
                                <Button size="sm" variant="ghost" className="text-red-500" onClick={() => setDeleteConfirm({ ...inv, type: 'invoice' })}><Trash2 className="w-3.5 h-3.5" /></Button>
                              </>
                            )}
                          </div>
                          {inv.status !== 'payee' && inv.status !== 'annulee' && (
                            <Select value={inv.status} onValueChange={(v) => handleStatusChange(inv, 'invoice', v)}>
                              <SelectTrigger className="w-32 h-7 text-xs"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                {INVOICE_STATUSES.map(s => (
                                  <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              )}
          </TabsContent>

        </Tabs>

        {/* Create/Edit Dialog */}
        <Dialog open={showForm} onOpenChange={setShowForm}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="billing-form-dialog">
            <DialogHeader>
              <DialogTitle data-testid="billing-form-title">
                {editItem ? (formType === 'quote' ? 'Modifier le devis' : 'Modifier la facture') : (formType === 'quote' ? 'Nouveau devis' : 'Nouvelle facture')}
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-4">
              {/* Client */}
              <div>
                <label className="text-sm font-medium text-slate-700">Client *</label>
                <ClientSearchSelect
                  clients={clients}
                  value={form.client_id}
                  onChange={(v) => { setForm(f => ({ ...f, client_id: v, vehicle_id: '' })); loadClientVehicles(v); }}
                  testId="select-client"
                />
              </div>

              {/* Vehicle */}
              {clientVehicles.length > 0 && (
                <div>
                  <label className="text-sm font-medium text-slate-700">Véhicule (optionnel)</label>
                  <Select value={form.vehicle_id || "none"} onValueChange={(v) => setForm(f => ({ ...f, vehicle_id: v === "none" ? "" : v }))}>
                    <SelectTrigger data-testid="select-vehicle"><SelectValue placeholder="Sélectionnez un véhicule" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">{t('common.none')}</SelectItem>
                      {clientVehicles.map(v => (
                        <SelectItem key={v.id} value={v.id}>{v.brand} {v.model} ({v.plate})</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Lines */}
              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className="text-sm font-medium text-slate-700">Lignes</label>
                  <div className="flex gap-1 flex-wrap">
                    {laborCatalog.length > 0 && (
                      <Popover open={laborPopoverOpen} onOpenChange={setLaborPopoverOpen}>
                        <PopoverTrigger asChild>
                          <Button size="sm" variant="outline" className="text-xs h-7" data-testid="add-labor-btn">
                            <Wrench className="w-3 h-3 mr-1" /> Main d'oeuvre
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-80 p-2 max-h-96 overflow-y-auto" align="end">
                          <p className="text-xs font-semibold text-slate-500 px-2 pb-1 mb-1 border-b">Catalogue travaux</p>
                          <div className="text-[10px] text-slate-400 px-2 pb-2 flex gap-3">
                            <span>Meca: {laborRates.mecanique || 140} CHF/h</span>
                            <span>Carro: {laborRates.carrosserie || 165} CHF/h</span>
                            <span>Panne: {laborRates.recherche_panne || 110} CHF/h</span>
                          </div>
                          {[...new Set(laborCatalog.map(l => l.group))].map(group => (
                            <div key={group} className="mb-2">
                              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider px-2 py-1">{group}</p>
                              {laborCatalog.filter(l => l.group === group).map((l, i) => (
                                <button key={i} onClick={() => { addLaborLine(l); setLaborPopoverOpen(false); }}
                                  className="w-full text-left px-2 py-1.5 text-sm rounded hover:bg-blue-50 flex justify-between items-center"
                                  data-testid={`add-labor-${l.name}`}>
                                  <span className="truncate text-slate-800">{l.name}</span>
                                  <span className="text-[10px] text-slate-500 font-mono ml-2 shrink-0">
                                    {l.default_hours}h x {laborRates[l.rate_type] || 140}
                                  </span>
                                </button>
                              ))}
                            </div>
                          ))}
                        </PopoverContent>
                      </Popover>
                    )}
                    <Popover open={partsPopoverOpen} onOpenChange={(o) => { setPartsPopoverOpen(o); if (!o) { setCatalogFilter(''); setPartsSearch(''); setPartsResults([]); } }}>
                      <PopoverTrigger asChild>
                        <Button size="sm" variant="outline" className="text-xs h-7" data-testid="add-part-btn">
                          <Package className="w-3 h-3 mr-1" /> Piece
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-80 p-2 max-h-96 overflow-y-auto" align="end">
                        <p className="text-xs font-semibold text-slate-500 px-2 pb-1 mb-1 border-b">Catalogue pieces standard</p>
                        <Input value={catalogFilter} onChange={e => setCatalogFilter(e.target.value)} placeholder="Filtrer..." className="h-7 text-xs mb-2" autoFocus data-testid="catalog-filter" />
                        {(() => {
                          const filtered = partsCatalog.filter(p => !catalogFilter || p.name.toLowerCase().includes(catalogFilter.toLowerCase()) || p.group.toLowerCase().includes(catalogFilter.toLowerCase()));
                          const groups = [...new Set(filtered.map(p => p.group))];
                          return groups.length === 0 ? (
                            <p className="text-xs text-slate-400 text-center py-2">Aucune piece trouvee</p>
                          ) : groups.map(group => (
                            <div key={group} className="mb-2">
                              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider px-2 py-1">{group}</p>
                              {filtered.filter(p => p.group === group).map((p, i) => (
                                <button key={i} onClick={() => addCatalogPart(p)}
                                  className="w-full text-left px-2 py-1.5 text-sm rounded hover:bg-emerald-50 flex justify-between items-center"
                                  data-testid={`catalog-part-${i}`}>
                                  <span className="truncate text-slate-800">{p.name}</span>
                                  <span className="text-[10px] text-slate-500 font-mono ml-2 shrink-0">{p.unit_price.toFixed(2)} CHF</span>
                                </button>
                              ))}
                            </div>
                          ));
                        })()}
                        {/* Also search inventory */}
                        <div className="border-t mt-2 pt-2">
                          <p className="text-[10px] text-slate-400 px-2 mb-1">Recherche inventaire</p>
                          <Input value={partsSearch} onChange={e => searchParts(e.target.value)} placeholder="Nom, ref, marque..." className="h-7 text-xs mb-1" data-testid="parts-search-input" />
                          {partsResults.map(p => (
                            <button key={p.id} onClick={() => addPartLine(p)}
                              className="w-full text-left px-2 py-1.5 text-sm rounded hover:bg-slate-100 flex justify-between items-center">
                              <div className="min-w-0">
                                <p className="truncate font-medium text-slate-800 text-xs">{p.name}</p>
                                <p className="text-[10px] text-slate-500">{p.brand} {p.reference ? `· ${p.reference}` : ''}</p>
                              </div>
                              <span className="text-[10px] font-mono text-slate-600 ml-2 shrink-0">{p.sell_price?.toFixed(2)} CHF</span>
                            </button>
                          ))}
                        </div>
                      </PopoverContent>
                    </Popover>
                    <Button size="sm" variant="outline" onClick={addLine} data-testid="add-line-btn"><Plus className="w-3 h-3 mr-1" /> Ligne</Button>
                  </div>
                </div>
                <div className="space-y-2">
                  {form.lines.map((line, idx) => (
                    <div key={idx} className="grid grid-cols-12 gap-2 items-end bg-slate-50 p-2 rounded" data-testid={`line-${idx}`}>
                      <div className="col-span-5">
                        {idx === 0 && <label className="text-xs text-slate-500">{t('common.description')}</label>}
                        <Input value={line.description} onChange={e => updateLine(idx, 'description', e.target.value)}
                          placeholder="Description" data-testid={`line-desc-${idx}`} />
                      </div>
                      <div className="col-span-2">
                        {idx === 0 && <label className="text-xs text-slate-500">Qte/Heures</label>}
                        <Input type="number" step="0.1" min="0" value={line.quantity} onChange={e => updateLine(idx, 'quantity', parseFloat(e.target.value) || 0)}
                          data-testid={`line-qty-${idx}`} />
                      </div>
                      <div className="col-span-2">
                        {idx === 0 && <label className="text-xs text-slate-500">Prix CHF</label>}
                        <Input type="number" step="0.01" min="0" value={line.unit_price} onChange={e => updateLine(idx, 'unit_price', parseFloat(e.target.value) || 0)}
                          data-testid={`line-price-${idx}`} />
                      </div>
                      <div className="col-span-2">
                        {idx === 0 && <label className="text-xs text-slate-500">TVA %</label>}
                        <Input type="number" step="0.1" value={line.tva_rate} onChange={e => updateLine(idx, 'tva_rate', parseFloat(e.target.value) || 0)}
                          data-testid={`line-tva-${idx}`} />
                      </div>
                      <div className="col-span-1 flex justify-center">
                        {form.lines.length > 1 && (
                          <Button size="sm" variant="ghost" className="text-red-500" onClick={() => removeLine(idx)}><XCircle className="w-4 h-4" /></Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                {/* Totals */}
                <div className="mt-3 flex justify-end">
                  <div className="bg-slate-100 rounded-lg p-3 text-right space-y-1 min-w-[200px]">
                    <p className="text-sm text-slate-600">Sous-total HT: <span className="font-medium">CHF {totals.subtotal}</span></p>
                    <p className="text-sm text-slate-600">TVA: <span className="font-medium">CHF {totals.tva}</span></p>
                    <p className="text-base font-bold text-slate-900">Total TTC: CHF {totals.total}</p>
                  </div>
                </div>
              </div>

              {/* Conditions & Notes */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-slate-700">Conditions</label>
                  <Input value={form.conditions} onChange={e => setForm(f => ({ ...f, conditions: e.target.value }))} data-testid="input-conditions" />
                </div>
                {formType === 'quote' && (
                  <div>
                    <label className="text-sm font-medium text-slate-700">Validite (jours)</label>
                    <Input type="number" value={form.validity_days} onChange={e => setForm(f => ({ ...f, validity_days: parseInt(e.target.value) || 30 }))} data-testid="input-validity" />
                  </div>
                )}
                {formType === 'invoice' && (
                  <div>
                    <label className="text-sm font-medium text-slate-700">Delai de paiement</label>
                    <Input type="date" value={form.payment_due_date || ''} onChange={e => setForm(f => ({ ...f, payment_due_date: e.target.value }))} data-testid="input-payment-due-date" />
                  </div>
                )}
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Notes</label>
                <Input value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} placeholder="Notes internes..." data-testid="input-notes" />
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setShowForm(false)}>{t('common.cancel')}</Button>
                <Button onClick={handleSave} data-testid="save-billing-btn">
                  {editItem ? 'Mettre a jour' : 'Creer'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation */}
        <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
          <DialogContent data-testid="delete-confirm-dialog">
            <DialogHeader>
              <DialogTitle>Confirmer la suppression</DialogTitle>
            </DialogHeader>
            <p className="text-sm text-slate-600">
              Supprimer {deleteConfirm?.type === 'quote' ? 'le devis' : 'la facture'} <strong>{deleteConfirm?.number}</strong> ?
            </p>
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="outline" onClick={() => setDeleteConfirm(null)}>{t('common.cancel')}</Button>
              <Button variant="destructive" onClick={handleDelete} data-testid="confirm-delete-btn">{t('common.delete')}</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default BillingPage;
